// WebSocket-клиент одной сессии. Реализует PROTOCOL.md §4 и §7:
// авто-реконнект с экспоненциальной задержкой и джиттером, since = последний
// полученный seq, ping/pong раз в 5 с для честного RTT.

import { streamUrl } from '../api'
import type { ClientMessage, PermissionMode, ServerMessage } from '../protocol'
import { logStore } from '../store/logStore'

/** Задержки переподключения, потолок 8 с (PROTOCOL §7). */
const BACKOFF_MS = [500, 1000, 2000, 4000, 8000]
const JITTER = 0.2
const PING_MS = 5000
/** Молчание сервера дольше этого — считаем связь мёртвой и переподключаемся. */
const PONG_TIMEOUT_MS = 20000
/**
 * Сколько ждём pong на пробном пинге при возврате на вкладку. Секунды, а не
 * PONG_TIMEOUT_MS: человек смотрит на панель прямо сейчас и ждёт лога, а не
 * надписи «переподключение…».
 */
const WAKE_PROBE_MS = 3000
/**
 * Минимальный зазор между попытками соединения, форсированными пробуждением.
 *
 * `visibilitychange`, `online` и `pageshow` приходят пачками: мигнула сеть,
 * человек дёрнул шторку уведомлений, iOS вернул вкладку из bfcache — и на один
 * реальный возврат прилетает десяток событий. Раньше каждое из них обнуляло
 * бэкофф и било новым сокетом, так что при лежащем сервере серия событий
 * превращалась в серию попыток. Зазор небольшой намеренно: возврат после паузы
 * (а это нормальный случай — человек был в другом приложении дольше секунды)
 * по-прежнему переподключается мгновенно, схлопывается только пачка.
 */
const WAKE_MIN_GAP_MS = 1000
/** Кадр применения входящих сообщений, если requestAnimationFrame не пришёл. */
const FLUSH_FALLBACK_MS = 120

/** Коды закрытия, после которых переподключаться бессмысленно (api/ws.py). */
const FATAL_CLOSE: Record<number, string> = {
  4401: 'сессия оператора истекла — войдите в панель заново',
  4404: 'сессия на сервере не найдена — окно можно закрыть',
}

function uid(): string {
  const c = globalThis.crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  return `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`
}

// ---------------- доставка реплик ----------------
//
// Реплика считается доставленной, только когда сервер вернул её эхом (строка
// kind="me" — сервер пишет её сразу при приёме). До этого она лежит в
// localStorage и переотправляется при каждом открытии сокета; сервер отсекает
// повторы по clientId. Так реплика переживает полуживой сокет (send «уходит»,
// но никуда не доходит), закрытое окно беседы и перезагрузку страницы.
//
// Повод — голосовое на 6 минут 21.09.2026: расшифровка шла 134 с, окно за это
// время переподключилось, и текст отправился в мёртвый стрим. Лог беседы
// остался пустым, а на экране висело «расшифровка отправлена агенту».

const UNSENT_PREFIX = 'agentbox.unsent.'
/** Сколько ждать эха, прежде чем считать сокет полуживым и переподключиться. */
const ACK_TIMEOUT_MS = 10_000

/** Что уходит вместе с репликой: вложения (id с сервера) и заметка «в контексте». */
export interface InputExtra {
  attachments?: string[]
  note?: string
  /** метаданные вложений для оптимистичного эха (превью до ответа сервера) */
  shown?: import('../protocol').AttachmentMeta[]
  /** большие вставки, чьи токены стоят в тексте */
  pastes?: import('../protocol').PastedText[]
}

interface Unsent {
  clientId: string
  text: string
  at: number
  attachments?: string[]
  note?: string
  pastes?: import('../protocol').PastedText[]
}

function loadUnsent(sessionId: string): Unsent[] {
  try {
    const raw = localStorage.getItem(UNSENT_PREFIX + sessionId)
    const list = raw ? (JSON.parse(raw) as Unsent[]) : []
    return Array.isArray(list) ? list.filter((u) => u && typeof u.text === 'string') : []
  } catch {
    return []
  }
}

function saveUnsent(sessionId: string, list: Unsent[]): void {
  try {
    if (list.length) localStorage.setItem(UNSENT_PREFIX + sessionId, JSON.stringify(list))
    else localStorage.removeItem(UNSENT_PREFIX + sessionId)
  } catch { /* приватный режим — живём без страховки */ }
}

/** Открытые стримы по беседам — чтобы доставить реплику, даже если окно пересоздалось. */
const liveStreams = new Map<string, SessionStream>()

/**
 * Отдать реплику агенту беседы, не завися от того, живо ли окно, которое её
 * набрало. `parked` — окна беседы сейчас нет: реплика ждёт в localStorage и
 * уйдёт, как только беседу откроют.
 */
export function deliverInput(
  sessionId: string, text: string, extra: InputExtra = {},
): 'sent' | 'parked' {
  const value = text.trim()
  const files = extra.attachments?.length ? extra.attachments : undefined
  if (!value && !files) return 'sent'
  const stream = liveStreams.get(sessionId)
  if (stream) {
    stream.send(value, extra)
    return 'sent'
  }
  saveUnsent(sessionId, [...loadUnsent(sessionId), {
    clientId: uid(), text: value, at: Date.now(), attachments: files, note: extra.note || undefined,
    pastes: extra.pastes?.length ? extra.pastes : undefined,
  }])
  return 'parked'
}

function inputMessage(u: Unsent): ClientMessage {
  return {
    type: 'input',
    text: u.text,
    clientId: u.clientId,
    ...(u.attachments?.length ? { attachments: u.attachments } : {}),
    ...(u.note ? { note: u.note } : {}),
    ...(u.pastes?.length ? { pastes: u.pastes } : {}),
  }
}

export interface PermissionOptions {
  message?: string | null
  remember?: boolean
  /** Для AskUserQuestion и подобных: выбор оператора, с которым тул выполнится. */
  updatedInput?: Record<string, unknown> | null
}

export class SessionStream {
  private ws: WebSocket | null = null
  private disposed = false
  private attempt = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private pingTimer: ReturnType<typeof setInterval> | null = null
  private lastPongAt = 0
  /** сообщения, накопленные пока сокет не открыт */
  private outbox: ClientMessage[] = []
  /** входящие сообщения текущего кадра */
  private queue: ServerMessage[] = []
  private raf = 0
  private flushTimer: ReturnType<typeof setTimeout> | null = null
  private wakeTimer: ReturnType<typeof setTimeout> | null = null
  private wakeBound = false
  /** когда в последний раз открывали сокет — для зазора между пробуждениями */
  private lastConnectAt = 0
  /** на какой момент времени заведён отложенный реконнект */
  private reconnectAt = 0

  /** реплики, по которым ещё не пришло эхо (зеркало localStorage) */
  private unacked: Unsent[] = []
  private ackTimer: ReturnType<typeof setInterval> | null = null

  constructor(private readonly sessionId: string) {}

  // ---------------- жизненный цикл ----------------

  start(): void {
    if (this.disposed || this.ws) return
    logStore.getState().ensure(this.sessionId)
    liveStreams.set(this.sessionId, this)
    // недоставленное прошлыми окнами — уйдёт при открытии сокета
    this.unacked = loadUnsent(this.sessionId)
    this.ackTimer = setInterval(() => this.checkAcks(), 2_000)
    this.bindWake()
    this.connect()
  }

  /** Полный teardown: сокет, таймеры, очередь. Утечь ничего не должно. */
  dispose(): void {
    this.disposed = true
    if (liveStreams.get(this.sessionId) === this) liveStreams.delete(this.sessionId)
    if (this.ackTimer !== null) { clearInterval(this.ackTimer); this.ackTimer = null }
    // недоставленные реплики остаются в localStorage — их доставит следующее окно
    this.unbindWake()
    this.clearReconnect()
    this.stopPing()
    if (this.wakeTimer !== null) { clearTimeout(this.wakeTimer); this.wakeTimer = null }
    if (this.flushTimer !== null) { clearTimeout(this.flushTimer); this.flushTimer = null }
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = 0 }
    this.queue = []
    this.outbox = []
    const ws = this.ws
    this.ws = null
    if (ws) {
      ws.onopen = null
      ws.onmessage = null
      ws.onerror = null
      ws.onclose = null
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        try { ws.close(1000, 'pane closed') } catch { /* уже закрыт */ }
      }
    }
  }

  // ---------------- исходящие ----------------

  /** Отправить ввод оператора. Оптимистичное эхо рисуется сразу (NFR-2). */
  send(text: string, extra: InputExtra = {}): void {
    const value = text.trim()
    const files = extra.attachments?.length ? extra.attachments : undefined
    if (!value && !files) return
    const clientId = uid()
    logStore.getState().pushOptimistic(this.sessionId, value, clientId, extra.shown, extra.pastes)
    const unsent: Unsent = {
      clientId, text: value, at: Date.now(), attachments: files, note: extra.note || undefined,
      pastes: extra.pastes?.length ? extra.pastes : undefined,
    }
    this.unacked.push(unsent)
    saveUnsent(this.sessionId, this.unacked)
    this.push(inputMessage(unsent))
  }

  /** Эхо «me» от сервера — реплика принята, из страховки её можно убрать.
   *  Новый сервер возвращает clientId; старый — только текст. */
  private ack(text: string | null | undefined, clientId?: string | null): void {
    const want = (text ?? '').trim()
    const byId = clientId ? this.unacked.findIndex((u) => u.clientId === clientId) : -1
    const i = byId !== -1 ? byId : this.unacked.findIndex((u) => u.text.trim() === want)
    if (i === -1) return
    this.unacked.splice(i, 1)
    saveUnsent(this.sessionId, this.unacked)
  }

  /** Эха нет дольше ACK_TIMEOUT_MS при «открытом» сокете — он полуживой, переподключаемся. */
  private checkAcks(): void {
    const ws = this.ws
    if (this.disposed || !ws || ws.readyState !== WebSocket.OPEN || !this.unacked.length) return
    const oldest = Math.min(...this.unacked.map((u) => u.at))
    if (Date.now() - oldest < ACK_TIMEOUT_MS) return
    // отсчёт заново, чтобы не рвать новый сокет, пока он поднимается
    const now = Date.now()
    this.unacked = this.unacked.map((u) => ({ ...u, at: now }))
    this.attempt = 0
    try { ws.close(4000, 'нет подтверждения реплики') } catch { /* уже закрыт */ }
  }

  interrupt(): void {
    this.push({ type: 'interrupt' })
  }

  respondPermission(id: string, behavior: 'allow' | 'deny', opts: PermissionOptions = {}): void {
    logStore.getState().markPermAnswered(
      this.sessionId, id, behavior === 'allow' ? 'allowed' : 'denied',
    )
    this.push({
      type: 'perm_response',
      id,
      behavior,
      message: opts.message ?? null,
      remember: opts.remember ?? false,
      updatedInput: opts.updatedInput ?? null,
    })
  }

  setPermissionMode(mode: PermissionMode): void {
    // индикатор в шапке рисуется из серверного status, локально ничего не мажем
    this.push({ type: 'set_permission_mode', mode })
  }

  resize(cols: number, rows: number): void {
    this.push({ type: 'resize', cols, rows })
  }

  private push(msg: ClientMessage): void {
    if (this.disposed) return
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg))
      return
    }
    // связи нет — придержим до реконнекта (кроме ping: он протухнет)
    if (msg.type !== 'ping') this.outbox.push(msg)
  }

  // ---------------- соединение ----------------

  private connect(): void {
    if (this.disposed) return
    this.lastConnectAt = Date.now()
    const store = logStore.getState()
    store.setConn(this.sessionId, this.attempt === 0 ? 'connecting' : 'reconnecting')

    const since = store.lastSeq(this.sessionId)
    let ws: WebSocket
    try {
      ws = new WebSocket(streamUrl(this.sessionId, since))
    } catch {
      this.scheduleReconnect()
      return
    }
    this.ws = ws

    ws.onopen = () => {
      if (this.disposed || this.ws !== ws) return
      this.attempt = 0
      this.lastPongAt = Date.now()
      logStore.getState().setConn(this.sessionId, 'live')
      logStore.getState().setError(this.sessionId, null)
      // реплики берём из страховки (она полнее outbox и переживает окна),
      // остальное — из outbox как было
      const outbox = this.outbox.filter((m) => m.type !== 'input')
      this.outbox = []
      for (const msg of outbox) ws.send(JSON.stringify(msg))
      const now = Date.now()
      for (const u of this.unacked) {
        u.at = now
        ws.send(JSON.stringify(inputMessage(u)))
      }
      this.startPing()
    }

    ws.onmessage = (ev) => {
      if (this.disposed || this.ws !== ws) return
      let msg: ServerMessage
      try {
        msg = JSON.parse(typeof ev.data === 'string' ? ev.data : '') as ServerMessage
      } catch {
        return
      }
      if (msg && msg.type === 'pong') {
        this.lastPongAt = Date.now()
        // t — наше же время отправки, вернувшееся эхом
        const rtt = Math.round(Date.now() - msg.t)
        if (Number.isFinite(rtt) && rtt >= 0 && rtt < 60000) {
          logStore.getState().setRtt(this.sessionId, rtt)
        }
        return
      }
      if (msg && msg.type === 'line' && msg.line?.kind === 'me') this.ack(msg.line.text, msg.line.clientId)
      this.queue.push(msg)
      this.scheduleFlush()
    }

    ws.onerror = () => {
      // подробности придут в onclose; сюда браузер не кладёт причину
    }

    ws.onclose = (ev) => {
      if (this.disposed || this.ws !== ws) return
      this.ws = null
      this.stopPing()
      this.flush()

      const store = logStore.getState()
      const slice = store.sessions[this.sessionId]
      if (slice?.conn === 'dead') return // фатальная ошибка — не долбимся

      const fatal = FATAL_CLOSE[ev.code]
      if (fatal) {
        store.setError(this.sessionId, fatal)
        store.pushLocal(this.sessionId, { kind: 'warn', gutter: null, text: fatal })
        store.setConn(this.sessionId, 'dead')
        return
      }
      // сессия на сервере завершилась — стрим больше не откроется
      if (slice?.session?.state === 'exited') {
        store.setConn(this.sessionId, 'dead')
        return
      }

      store.setConn(this.sessionId, 'reconnecting')
      this.scheduleReconnect()
    }
  }

  private scheduleReconnect(): void {
    if (this.disposed || this.reconnectTimer !== null) return
    const base = BACKOFF_MS[Math.min(this.attempt, BACKOFF_MS.length - 1)]
    const delay = base * (1 + (Math.random() * 2 - 1) * JITTER)
    this.attempt += 1
    this.armReconnect(delay)
  }

  /** Завести отложенный реконнект на `delay` мс, не трогая счётчик попыток. */
  private armReconnect(delay: number): void {
    if (this.disposed || this.reconnectTimer !== null) return
    this.reconnectAt = Date.now() + delay
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.connect()
    }, delay)
  }

  private clearReconnect(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  // ---------------- возврат на вкладку ----------------

  // Телефон: вкладка ушла в фон — система тормозит таймеры и рвёт сокет молча.
  // Сокет при этом может ещё числиться OPEN, а ping-интервал — не тикать, поэтому
  // сами мы узнавали о разрыве только через PONG_TIMEOUT_MS, да ещё и с бэкоффом
  // сверху: до 28 с «переподключение…» на экране у человека, который уже смотрит
  // на панель. Возврат на вкладку и появление сети — это точный сигнал «проверь
  // связь прямо сейчас», им и пользуемся.

  private readonly onWake = (): void => {
    if (this.disposed) return
    // visibilitychange стреляет и на уход в фон — там проверять нечего
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
    this.checkAlive()
  }

  private bindWake(): void {
    if (this.wakeBound || typeof document === 'undefined') return
    document.addEventListener('visibilitychange', this.onWake)
    window.addEventListener('online', this.onWake)
    // iOS возвращает вкладку из bfcache без visibilitychange
    window.addEventListener('pageshow', this.onWake)
    this.wakeBound = true
  }

  private unbindWake(): void {
    if (!this.wakeBound) return
    document.removeEventListener('visibilitychange', this.onWake)
    window.removeEventListener('online', this.onWake)
    window.removeEventListener('pageshow', this.onWake)
    this.wakeBound = false
  }

  /** Форсированная проверка живости соединения без ожидания таймаутов. */
  private checkAlive(): void {
    // 'dead' — это истёкшая сессия оператора или завершившийся агент: там сокет
    // закрыт осознанно, и долбиться в него по возврату на вкладку нельзя.
    if (logStore.getState().sessions[this.sessionId]?.conn === 'dead') return
    const ws = this.ws
    if (!ws) {
      // Сокета нет: либо ждём отложенный реконнект (до 8 с), либо реконнект не
      // ставился вовсе. Раз человек вернулся — пробуем немедленно и с чистым
      // бэкоффом: сеть после разблокировки телефона обычно уже другая, копить
      // задержку за прошлые неудачи бессмысленно.
      //
      // «Немедленно» — но не чаще, чем раз в WAKE_MIN_GAP_MS: события
      // пробуждения приходят пачками, и без зазора пачка становится пачкой
      // сокетов. Если с прошлой попытки прошло меньше зазора, событие не
      // выбрасываем, а подтягиваем к нему отложенный реконнект — и не отодвигаем
      // его, если бэкофф уже назначил попытку раньше.
      const now = Date.now()
      const wait = this.lastConnectAt + WAKE_MIN_GAP_MS - now
      if (wait <= 0) {
        this.attempt = 0
        this.clearReconnect()
        this.connect()
        return
      }
      if (this.reconnectTimer !== null && this.reconnectAt <= now + wait) return
      this.clearReconnect()
      this.armReconnect(wait)
      return
    }
    if (ws.readyState === WebSocket.CONNECTING) return // и так в процессе
    if (ws.readyState !== WebSocket.OPEN) {
      // Закрывается: onclose отработает сам, нам достаточно обнулить бэкофф,
      // чтобы он поставил реконнект на ближайшие полсекунды.
      this.attempt = 0
      return
    }

    // Сокет числится открытым, но после сна это ничего не доказывает: половина
    // мёртвых соединений выглядит живыми, пока в них не напишешь. Пишем — ping —
    // и даём короткий срок на ответ.
    if (this.wakeTimer !== null) return // проба уже идёт
    const sentAt = Date.now()
    // Пока таймеры были заморожены, pong не приходил не из-за обрыва. Обнуляем
    // отсчёт, иначе штатный ping-интервал прибьёт заведомо живой сокет на первом
    // же тике и мы получим лишний реконнект вместо проверки.
    this.lastPongAt = sentAt
    try {
      const ping: ClientMessage = { type: 'ping', t: sentAt }
      ws.send(JSON.stringify(ping))
    } catch {
      this.attempt = 0
      try { ws.close(4000, 'wake: сокет не принимает данные') } catch { /* уже закрыт */ }
      return
    }
    this.startPing() // интервал мог быть заморожен в фоне — заводим заново
    this.wakeTimer = setTimeout(() => {
      this.wakeTimer = null
      if (this.disposed || this.ws !== ws) return
      if (this.lastPongAt > sentAt) return // pong дошёл — связь настоящая
      this.attempt = 0
      try { ws.close(4000, 'wake: нет pong') } catch { /* уже закрыт */ }
    }, WAKE_PROBE_MS)
  }

  // ---------------- ping / RTT ----------------

  private startPing(): void {
    this.stopPing()
    this.pingTimer = setInterval(() => {
      if (this.disposed) return
      const ws = this.ws
      if (!ws || ws.readyState !== WebSocket.OPEN) return
      if (Date.now() - this.lastPongAt > PONG_TIMEOUT_MS) {
        // сервер молчит — рвём сами, дальше отработает реконнект
        try { ws.close(4000, 'pong timeout') } catch { /* уже закрыт */ }
        return
      }
      const ping: ClientMessage = { type: 'ping', t: Date.now() }
      ws.send(JSON.stringify(ping))
    }, PING_MS)
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer)
      this.pingTimer = null
    }
  }

  // ---------------- применение входящих ----------------

  /** Копим сообщения кадра и применяем одним set — меньше рендеров при стриме. */
  private scheduleFlush(): void {
    if (this.raf || this.flushTimer !== null) return
    this.raf = requestAnimationFrame(() => {
      this.raf = 0
      if (this.flushTimer !== null) { clearTimeout(this.flushTimer); this.flushTimer = null }
      this.flush()
    })
    // вкладка в фоне — rAF не приходит, страхуемся таймером
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null
      if (this.raf) { cancelAnimationFrame(this.raf); this.raf = 0 }
      this.flush()
    }, FLUSH_FALLBACK_MS)
  }

  private flush(): void {
    if (this.queue.length === 0) return
    const batch = this.queue
    this.queue = []
    logStore.getState().applyMany(this.sessionId, batch)
  }
}
